import { RefObject, useEffect, useRef, useState } from "react";
import { averagePoseVisibility, drawPremiumSkeleton, drawSpatialPose, fitPoseStage, type PosePoint } from "./aiEdgePose";

type WorkerMessage =
  | { type: "ready"; delegate: "GPU" | "CPU" }
  | { type: "result"; landmarks: PosePoint[]; worldLandmarks: PosePoint[]; timestampMs: number }
  | { type: "error"; message: string };

export function AiEdgePosePreview({ src, videoRef, enabled }: { src: string; videoRef: RefObject<HTMLVideoElement | null>; enabled: boolean }) {
  const stageRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const spatialRef = useRef<HTMLCanvasElement>(null);
  const workerRef = useRef<Worker | null>(null);
  const framePending = useRef(false);
  const animationFrame = useRef(0);
  const lastVideoTime = useRef(-1);
  const lastWallClock = useRef(0);
  const workerTimestamp = useRef(0);
  const [status, setStatus] = useState<"consent" | "loading" | "ready" | "unavailable">(enabled ? "loading" : "consent");
  const [delegate, setDelegate] = useState<"GPU" | "CPU" | null>(null);
  const [visibility, setVisibility] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    const stage = stageRef.current;
    const overlay = overlayRef.current;
    const spatial = spatialRef.current;
    if (!video || !stage || !overlay || !spatial) return;

    if (!enabled) {
      overlay.getContext("2d")?.clearRect(0, 0, overlay.width, overlay.height);
      spatial.getContext("2d")?.clearRect(0, 0, spatial.width, spatial.height);
      setStatus("consent");
      return;
    }
    setStatus("loading");
    lastVideoTime.current = -1;
    lastWallClock.current = 0;
    workerTimestamp.current = 0;

    const resize = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        const aspectRatio = video.videoWidth / video.videoHeight;
        const availableWidth = stage.parentElement?.clientWidth ?? video.clientWidth;
        // Make the stage itself match the media aspect ratio. This avoids the
        // letterbox offset that otherwise moves normalized pose coordinates
        // away from the athlete on portrait and very wide recordings.
        stage.style.width = `${fitPoseStage(availableWidth, aspectRatio).width}px`;
        stage.style.aspectRatio = `${video.videoWidth} / ${video.videoHeight}`;
      }
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      overlay.width = Math.max(1, Math.round(stage.clientWidth * ratio));
      overlay.height = Math.max(1, Math.round(stage.clientHeight * ratio));
      spatial.width = Math.round(176 * ratio);
      spatial.height = Math.round(124 * ratio);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(video);
    video.addEventListener("loadedmetadata", resize);
    resize();

    const worker = new Worker(new URL("./poseWorker.ts", import.meta.url), { type: "module" });
    workerRef.current = worker;
    worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
      if (event.data.type === "ready") {
        setStatus("ready");
        setDelegate(event.data.delegate);
        return;
      }
      if (event.data.type === "error") {
        framePending.current = false;
        setStatus("unavailable");
        return;
      }
      framePending.current = false;
      const overlayContext = overlay.getContext("2d");
      const spatialContext = spatial.getContext("2d");
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      if (overlayContext) drawPremiumSkeleton(overlayContext, event.data.landmarks, { pixelRatio: ratio });
      if (spatialContext) drawSpatialPose(spatialContext, event.data.worldLandmarks, { pixelRatio: ratio });
      setVisibility(averagePoseVisibility(event.data.landmarks));
    };
    worker.postMessage({ type: "initialize" });

    const render = async () => {
      if (!video.paused && !video.ended && video.readyState >= 2 && !framePending.current) {
        const now = performance.now();
        if (video.currentTime !== lastVideoTime.current && now - lastWallClock.current >= 80) {
          framePending.current = true;
          lastVideoTime.current = video.currentTime;
          lastWallClock.current = now;
          workerTimestamp.current += 81;
          try {
            const frame = await createImageBitmap(video);
            worker.postMessage({ type: "frame", frame, timestampMs: workerTimestamp.current }, [frame]);
          } catch {
            framePending.current = false;
          }
        }
      }
      animationFrame.current = requestAnimationFrame(render);
    };
    animationFrame.current = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animationFrame.current);
      observer.disconnect();
      video.removeEventListener("loadedmetadata", resize);
      worker.terminate();
      workerRef.current = null;
    };
  }, [src, videoRef, enabled]);

  return <div ref={stageRef} className="ai-edge-stage">
    <video ref={videoRef} src={src} controls preload="metadata" playsInline />
    <canvas ref={overlayRef} className="ai-edge-overlay" aria-hidden="true" />
    <canvas ref={spatialRef} className="ai-edge-spatial" aria-label="Projeção espacial 3D da pose" />
    <span className={`ai-edge-badge ${status}`}>
      {status === "consent" ? "AI Edge aguarda autorização" : status === "loading" ? "AI Edge preparando…" : status === "unavailable" ? "AI Edge indisponível" : `AI Edge · ${delegate} · pose ${Math.round(visibility * 100)}%`}
    </span>
  </div>;
}
